import React, { useState, useCallback, useEffect } from 'react';
import CryptoJS from 'crypto-js';
// FIX: Import InventoryItemUpdate and NewAttachment to use in the handleUpdate function signature.
import { Page, PageId, PageStatus, PageWithStatus, AdmissionData, InventoryItemUpdate, NewAttachment, NewSignature } from './types';
import Sidebar from './components/Sidebar';
import AdmissionPage from './components/content/AdmissionPage';
import ServiceAgreementPage from './components/content/ServiceAgreementPage';
import Schedule1Page from './components/content/Schedule1Page';
import Schedule2Page from './components/content/Schedule2Page';
import Schedule3Page from './components/content/Schedule3Page';
import AcceptancePage from './components/content/AcceptancePage';
import PDAPage from './components/content/PDPAPage';
import WelcomePage from './components/content/WelcomePage';
import ReportView from './components/content/ReportView';
import { AdmissionIcon, PDPAIcon, ServiceAgreementIcon, WelcomeIcon, DownloadIcon } from './components/Icons';
import { API_URL_CMP, API_KEY_CMP, encryptedCustomerId, hasStaffPrivileges } from './user_constant';

const pages: Page[] = [
  { id: 'admission', title: 'Admission', level: 1, IconComponent: AdmissionIcon },
  { id: 'serviceAgreement', title: 'Service Agreement', level: 1, IconComponent: ServiceAgreementIcon },
  { id: 'schedule1', title: 'SA - Schedule 1', level: 2 },
  { id: 'schedule2', title: 'SA - Schedule 2', level: 2 },
  { id: 'schedule3', title: 'SA - Schedule 3', level: 2 },
  { id: 'acceptance', title: 'SA Acceptance', level: 2 },
  { id: 'pdpa', title: 'PDPA', level: 1, IconComponent: PDPAIcon },
  { id: 'welcome', title: 'Welcome', level: 1, IconComponent: WelcomeIcon },
  { id: 'download', title: 'Download Report', level: 1, IconComponent: DownloadIcon },
];

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

// --- Completeness Calculation Logic ---
const isFilled = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (typeof value === 'number' && isNaN(value)) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

const getPageCompleteness = (pageId: PageId, data: AdmissionData): number => {
  let completed = 0;
  let total = 0;

  switch (pageId) {
    case 'admission':
      const fields = [
        data.date_of_admission, data.room_assigned, data.cr_name, data.cr_ic_passport,
        data.cr_mobile, data.cr_address, data.cr_height, data.cr_weight,
        data.cr_ambulatory, data.cr_gender, data.cr_language, data.cr_dob,
        data.cr_email, data.co_name, data.co_ic_passport, data.co_mobile,
        data.co_address, data.co_gender, data.co_dob, data.diapers,
        data.medication, data.milk_powder, data.follow_up_hospital,
        data.follow_up_doctor_name, data.follow_up_doctor_contact,
        data.emergency_hospital, data.emergency_name, data.emergency_mobile,
        data.emergency_relationship
      ];
      total = fields.length;
      completed = fields.filter(isFilled).length;

      total += 1;
      const confirmationSign = Array.isArray(data.signatures)
        ? data.signatures.find(s => s.type === 'admission_co_type')
        : null;
      if (confirmationSign?.signature_url_s3) {
        completed += 1;
      }
      break;

    case 'schedule1':
      const s1Fields = [
        data.cr_name, data.cr_ic_passport, data.cr_mobile, data.cr_address,
        data.co_name, data.co_ic_passport, data.co_mobile, data.co_address,
        data.follow_up_hospital,
        data.date_of_agreement, data.services, data.room_assigned, data.term, data.commencement_date, data.security_deposit
      ];
      total = s1Fields.length;
      completed = s1Fields.filter(isFilled).length;
      break;

    case 'schedule2':
      const s2Fields = [
        data.room_type, data.price_per_month, data.deposit, data.services_schedule_2,
        data.others, data.total, data.commencement_date_schedule_2, data.payment_terms
      ];
      total = s2Fields.length;
      completed = s2Fields.filter(isFilled).length;
      break;

    case 'schedule3':
      total = 5;
      const guestCheckin = Array.isArray(data.signatures)
        ? data.signatures.find(s => s.type === 'guest_checkin_type' || s.type === 'checkin_guest')
        : null;
      const staffCheckin = Array.isArray(data.signatures)
        ? data.signatures.find(s => s.type === 'staff_checkin_type' || s.type === 'checkin_staff')
        : null;
      if (guestCheckin?.signature_url_s3) completed++;
      if (staffCheckin?.signature_url_s3) completed++;
      if (isFilled(staffCheckin?.date)) completed++;
      if (isFilled(staffCheckin?.name)) completed++;
      if (isFilled(staffCheckin?.position)) completed++;
      break;

    case 'acceptance':
      total = 2;
      const residentAcceptance = Array.isArray(data.signatures)
        ? data.signatures.find(s => s.type === 'cr_acceptance_type')
        : null;
      const suretyAcceptance = Array.isArray(data.signatures)
        ? data.signatures.find(s => s.type === 'co_acceptance_type')
        : null;
      if (residentAcceptance?.signature_url_s3) completed++;
      if (suretyAcceptance?.signature_url_s3) completed++;
      break;

    case 'pdpa':
      total = 1;
      const pdpaSign = Array.isArray(data.signatures)
        ? data.signatures.find(s => s.type === 'pdpa_co_type')
        : null;
      if (pdpaSign?.signature_url_s3) completed++;
      break;

    default:
      return 100;
  }

  if (total === 0) return 100;
  return Math.round((completed / total) * 100);
};

const calculateCompleteness = (data: AdmissionData): Record<PageId, number> => {
  const completeness: Record<string, number> = {};
  for (const page of pages) {
    completeness[page.id] = getPageCompleteness(page.id, data);
  }
  return completeness as Record<PageId, number>;
};

const calculateTotalCompleteness = (completeness: Record<PageId, number> | null): number => {
  const pagesToAverage: PageId[] = ['admission', 'schedule1', 'schedule2', 'schedule3', 'acceptance', 'pdpa'];
  if (!completeness) return 0;
  const total = pagesToAverage.reduce((acc, pageId) => acc + (completeness[pageId] || 0), 0);
  return Math.round(total / pagesToAverage.length);
};
// --- End of Completeness Logic ---

// ---------------------------------------------------------------------------
// Signature URL cache — persists saved signature_url_s3 values in localStorage
// so they survive page refreshes while the backend GET is being fixed.
// Key: `rc_sig_cache_${customerId}`, Value: Record<type, signature_url_s3>
// ---------------------------------------------------------------------------
const SIG_CACHE_PREFIX = 'rc_sig_cache_';

function getSigCache(customerId: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(SIG_CACHE_PREFIX + customerId);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setSigCache(customerId: string, updates: Record<string, string>) {
  try {
    const existing = getSigCache(customerId);
    localStorage.setItem(SIG_CACHE_PREFIX + customerId, JSON.stringify({ ...existing, ...updates }));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

// Normalize API payload to ensure arrays are always present,
// and merge any locally-cached signature URLs that the backend GET hasn't returned yet.
const normalizeAdmissionData = (data: AdmissionData): AdmissionData => {
  const signatures = Array.isArray(data.signatures) ? data.signatures : [];

  // Merge cached signature URLs into the GET response
  const customerId = data.customer_id || encryptedCustomerId() || '';
  const cache = getSigCache(customerId);
  const mergedSignatures = signatures.map(sig => {
    const cachedUrl = cache[sig.type];
    if (cachedUrl && !sig.signature_url_s3) {
      return { ...sig, signature_url_s3: cachedUrl };
    }
    return sig;
  });

  return {
    ...data,
    customer_id: customerId,
    signatures: mergedSignatures,
    attachments: Array.isArray(data.attachments) ? data.attachments : [],
    items: Array.isArray(data.items) ? data.items : [],
  };
};


const SaveStatusDisplay: React.FC<{ status: SaveStatus, message?: string }> = ({ status, message }) => {
  if (status === 'idle') return null;

  const config = {
    saving: { text: 'Saving...', color: 'text-slate-600', icon: <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> },
    success: { text: 'Saved successfully!', color: 'text-green-600', icon: <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg> },
    error: { text: `Save failed: ${message}`, color: 'text-red-600', icon: <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" /></svg> }
  };

  const current = config[status];

  return (
    <div className={`fixed bottom-5 right-5 z-50 flex items-center px-4 py-2 rounded-lg shadow-lg text-sm font-medium animate-fade-in-up ${current.color} bg-white border`}>
      {current.icon}
      <span>{current.text}</span>
    </div>
  );
};

const App: React.FC = () => {
  const [activePageId, setActivePageId] = useState<PageId>('admission');
  const [admissionData, setAdmissionData] = useState<AdmissionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState('');
  const [isClientView, setIsClientView] = useState(!hasStaffPrivileges());
  const [completeness, setCompleteness] = useState<Record<PageId, number> | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const reportError = useCallback((message: string) => {
    setSaveError(message);
    setSaveStatus('error');
    window.setTimeout(() => setSaveStatus('idle'), 5000);
  }, []);

  const fetchFormForCustomer = useCallback(async (customerId: string) => {
    const url = `${API_URL_CMP}/careadmin/resident-admission-form?customer_id=${encodeURIComponent(customerId)}`;
    const response = await fetch(url, {
      headers: {
        'x-api-key': API_KEY_CMP,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }, []);

  const createFormForCustomer = useCallback(async (customerId: string) => {
    const response = await fetch(`${API_URL_CMP}/careadmin/resident-admission-form`, {
      method: 'POST',
      headers: {
        'x-api-key': API_KEY_CMP,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ customer_id: customerId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `Create failed with status: ${response.status}` }));
      throw new Error(errorData.message || 'Create failed.');
    }

    return response.json();
  }, []);

  /**
   * Decrypt an AES-encrypted customer ID (CryptoJS format) to its plain numeric string.
   * Returns the original string unchanged if decryption fails or is not needed.
   */
  const decryptCustomerId = useCallback((encryptedId: string): string => {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedId, 'CareConcierge');
      const plain = bytes.toString(CryptoJS.enc.Utf8);
      return plain || encryptedId;
    } catch {
      return encryptedId;
    }
  }, []);

  /** Fetch a customer's profile from the CMP API. Returns null on failure (non-critical). */
  const fetchCustomerInfo = useCallback(async (encryptedId: string): Promise<any | null> => {
    try {
      const plainId = decryptCustomerId(encryptedId);
      console.log('[Admission] fetchCustomerInfo — plain customer ID:', plainId);
      const response = await fetch(`${API_URL_CMP}/careadmin/customers/${encodeURIComponent(plainId)}`, {
        headers: { 'x-api-key': API_KEY_CMP },
      });
      if (!response.ok) {
        console.warn('[Admission] fetchCustomerInfo — non-OK status:', response.status);
        return null;
      }
      const data = await response.json();
      console.log('[Admission] fetchCustomerInfo — response:', data);
      return Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
    } catch (e) {
      console.warn('[Admission] fetchCustomerInfo — error:', e);
      return null;
    }
  }, [decryptCustomerId]);

  /**
   * Map a customer API object to an AdmissionData partial so that a brand-new
   * form is pre-populated with information already on file.
   */
  const mapCustomerToAdmissionPayload = useCallback((customer: any): Partial<Omit<AdmissionData, 'items' | 'attachments' | 'signatures'>> => {
    const payload: Partial<Omit<AdmissionData, 'items' | 'attachments' | 'signatures'>> = {};

    // ── Resident name ──────────────────────────────────────────────────────────
    const fullName = customer.full_name
      || [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim()
      || null;
    if (fullName) payload.cr_name = fullName;

    // ── IC / Passport ──────────────────────────────────────────────────────────
    const icOrPassport = customer.ic_no || customer.passport || null;
    if (icOrPassport) payload.cr_ic_passport = icOrPassport;

    // ── Mobile ─────────────────────────────────────────────────────────────────
    if (customer.mobile) payload.cr_mobile = customer.mobile;

    // ── Email ──────────────────────────────────────────────────────────────────
    if (customer.email) payload.cr_email = customer.email;

    // ── Date of birth ──────────────────────────────────────────────────────────
    if (customer.dob) payload.cr_dob = customer.dob;

    // ── Gender ─────────────────────────────────────────────────────────────────
    if (customer.sex) payload.cr_gender = customer.sex.toLowerCase();

    // ── Address (combine address parts) ────────────────────────────────────────
    const addressParts = [
      customer.add_street,
      customer.add_street2,
      customer.add_city,
      customer.add_postcode,
      customer.add_state,
      customer.add_country,
    ].filter(Boolean);
    if (addressParts.length > 0) payload.cr_address = addressParts.join(', ');

    // ── Surety / Care-owner (highest-priority care owner from careowner_list) ──
    const careOwners: any[] = Array.isArray(customer.careowner_list) ? customer.careowner_list : [];
    const primaryCareOwner = careOwners.length > 0
      ? [...careOwners].sort((a, b) => (b.priority || 0) - (a.priority || 0))[0]
      : null;

    if (primaryCareOwner) {
      const coName = primaryCareOwner.full_name
        || [primaryCareOwner.first_name, primaryCareOwner.last_name].filter(Boolean).join(' ').trim()
        || null;
      if (coName) payload.co_name = coName;
      if (primaryCareOwner.ic_no || primaryCareOwner.passport)
        payload.co_ic_passport = primaryCareOwner.ic_no || primaryCareOwner.passport;
      if (primaryCareOwner.mobile) payload.co_mobile = primaryCareOwner.mobile;
      if (primaryCareOwner.email) {
        // store in special notes as there is no dedicated care-owner email field
        // only if no other special notes exist
      }
      if (primaryCareOwner.dob) payload.co_dob = primaryCareOwner.dob;
      if (primaryCareOwner.sex) payload.co_gender = primaryCareOwner.sex.toLowerCase();
      const coAddressParts = [
        primaryCareOwner.add_street,
        primaryCareOwner.add_street2,
        primaryCareOwner.add_city,
        primaryCareOwner.add_postcode,
        primaryCareOwner.add_state,
        primaryCareOwner.add_country,
      ].filter(Boolean);
      if (coAddressParts.length > 0) payload.co_address = coAddressParts.join(', ');
    }

    return payload;
  }, []);

  const fetchData = useCallback(async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const customerId = encryptedCustomerId() ?? "";
      if (!customerId) {
        throw new Error("Missing customer ID.");
      }

      const data = await fetchFormForCustomer(customerId);
      if (data && data.length > 0 && data[0].id) {
        // Form already exists — load it, then back-fill any empty fields from customer profile.
        let existingRecord = normalizeAdmissionData(data[0]);

        try {
          const customerInfo = await fetchCustomerInfo(customerId);
          if (customerInfo) {
            const prefillPayload = mapCustomerToAdmissionPayload(customerInfo);
            // Only apply fields that are currently empty in the saved record.
            const missingFields = Object.fromEntries(
              Object.entries(prefillPayload).filter(([key]) => {
                const v = (existingRecord as any)[key];
                return v === null || v === undefined || v === '' || v === 0;
              })
            ) as Partial<AdmissionData>;

            if (Object.keys(missingFields).length > 0) {
              try {
                const patchUrl = `${API_URL_CMP}/careadmin/resident-admission-form?id=${encodeURIComponent(existingRecord.id)}`;
                await fetch(patchUrl, {
                  method: 'PUT',
                  headers: { 'x-api-key': API_KEY_CMP, 'Content-Type': 'application/json' },
                  body: JSON.stringify(missingFields),
                });
                existingRecord = { ...existingRecord, ...missingFields };
              } catch (prefillError) {
                console.warn('[Admission] Failed to back-fill existing form from customer info:', prefillError);
              }
            }
          }
        } catch (customerFetchError) {
          console.warn('[Admission] Failed to fetch customer info for back-fill:', customerFetchError);
        }

        setAdmissionData(existingRecord);
      } else {
        const created = await createFormForCustomer(customerId);
        const createdRecord = Array.isArray(created) ? created[0] : created;

        // Resolve the newly created record (with id) so we can PATCH it.
        let newRecord: AdmissionData | null = null;
        if (createdRecord && createdRecord.id) {
          newRecord = normalizeAdmissionData(createdRecord);
        } else {
          const createdData = await fetchFormForCustomer(customerId);
          if (createdData && createdData.length > 0) {
            newRecord = normalizeAdmissionData(createdData[0]);
          } else {
            throw new Error("Form created but no data returned.");
          }
        }

        // Pre-populate the brand-new form with the customer's existing profile data.
        if (newRecord) {
          const customerInfo = await fetchCustomerInfo(customerId);
          if (customerInfo) {
            const prefillPayload = mapCustomerToAdmissionPayload(customerInfo);
            const hasPrefillData = Object.keys(prefillPayload).length > 0;
            if (hasPrefillData) {
              try {
                const patchUrl = `${API_URL_CMP}/careadmin/resident-admission-form?id=${encodeURIComponent(newRecord.id)}`;
                await fetch(patchUrl, {
                  method: 'PUT',
                  headers: { 'x-api-key': API_KEY_CMP, 'Content-Type': 'application/json' },
                  body: JSON.stringify(prefillPayload),
                });
                // Merge the prefill data into the local record so the form renders immediately
                newRecord = { ...newRecord, ...prefillPayload };
              } catch (prefillError) {
                // Non-critical — log and continue with un-prefilled form
                console.warn('[Admission] Failed to pre-populate new form from customer info:', prefillError);
              }
            }
          }
          setAdmissionData(newRecord);
        }
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : "An unknown error occurred";
      if (showLoader) {
        setError(errorMessage);
      } else {
        // For background refetches (like after an upload), don't unmount the whole page.
        // Log the error and show a non-disruptive notification.
        console.error("Background data refresh failed:", errorMessage);
        setSaveError(`Data refresh failed: ${errorMessage}`);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 5000);
      }
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [createFormForCustomer, fetchFormForCustomer, fetchCustomerInfo, mapCustomerToAdmissionPayload]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    if (admissionData) {
      setCompleteness(calculateCompleteness(admissionData));
    }
  }, [admissionData]);

  useEffect(() => {
    if (!isPrinting) return;
    const handleAfterPrint = () => {
      setIsPrinting(false);
      window.scrollTo({ top: 0, behavior: "auto" });
    };
    window.addEventListener("afterprint", handleAfterPrint);
    const totalHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    let currentScroll = 0;
    window.scrollTo({ top: 0, behavior: "auto" });
    let printTimerId: number | undefined;
    let scrollIntervalId: number;

    const scrollStep = () => {
      currentScroll += viewportHeight;
      if (currentScroll >= totalHeight - viewportHeight) {
        window.scrollTo({ top: totalHeight, behavior: "smooth" });
        window.clearInterval(scrollIntervalId);
        printTimerId = window.setTimeout(() => {
          window.print();
        }, 800);
      } else {
        window.scrollTo({ top: currentScroll, behavior: "smooth" });
      }
    };

    scrollIntervalId = window.setInterval(scrollStep, 300);

    return () => {
      window.removeEventListener("afterprint", handleAfterPrint);
      window.clearInterval(scrollIntervalId);
      if (printTimerId) {
        window.clearTimeout(printTimerId);
      }
    };
  }, [isPrinting]);


  const handleUpdate = useCallback(async (payload: Partial<Omit<AdmissionData, 'items' | 'attachments' | 'signatures'>> & { items?: InventoryItemUpdate[], attachments?: NewAttachment[], signatures?: NewSignature[] }) => {
    if (!admissionData?.id) {
      setSaveError("Missing admission ID.");
      setSaveStatus('error');
      return;
    }

    setSaveStatus('saving');
    setSaveError('');

    try {
      const url = `${API_URL_CMP}/careadmin/resident-admission-form?id=${encodeURIComponent(admissionData.id)}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'x-api-key': API_KEY_CMP,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Update failed with status: ' + response.status }));
        throw new Error(errorData.message || 'An unknown error occurred during update.');
      }

      // Persist saved signature URLs to localStorage so they survive page refreshes.
      if (payload.signatures && payload.signatures.length > 0) {
        const customerId = admissionData.customer_id || encryptedCustomerId() || '';
        const urlsToCache: Record<string, string> = {};
        for (const sig of payload.signatures) {
          if (sig.signature_url_s3) {
            urlsToCache[sig.type] = sig.signature_url_s3;
            // Also cache under legacy type aliases so GET response can match either name
            const legacyAliases: Record<string, string> = {
              checkin_guest: 'guest_checkin_type',
              checkin_staff: 'staff_checkin_type',
              checkout_guest: 'guest_checkout_type',
              checkout_staff: 'staff_checkout_type',
              guest_checkin_type: 'checkin_guest',
              staff_checkin_type: 'checkin_staff',
              guest_checkout_type: 'checkout_guest',
              staff_checkout_type: 'checkout_staff',
            };
            if (legacyAliases[sig.type]) {
              urlsToCache[legacyAliases[sig.type]] = sig.signature_url_s3;
            }
          }
        }
        if (Object.keys(urlsToCache).length > 0) {
          setSigCache(customerId, urlsToCache);
        }
      }

      // Instead of refetching data, perform an optimistic update on the local state.
      // This prevents the page from resetting and losing unsaved user input.
      setAdmissionData(prevData => {
        if (!prevData) return null;

        const { items, attachments, signatures, ...restOfPayload } = payload;

        // Merge inventory item updates
        const updatedItems = items
          ? prevData.items.map(section => ({
            ...section,
            inventories: section.inventories.map(item => {
              const update = items.find(newItem =>
                (newItem.id && newItem.id === item.id) ||
                (!newItem.id && newItem.name === item.title)
              );
              if (update) {
                return { ...item, isCheckIn: update.check_in, isCheckOut: update.check_out };
              }
              return item;
            })
          }))
          : prevData.items;

        // Add new attachments with a temporary local ID for rendering
        const newAttachments = attachments?.map(att => ({
          ...att,
          id: `temp-${Date.now()}-${Math.random()}`
        })) || [];
        const updatedAttachments = attachments ? [...prevData.attachments, ...newAttachments] : prevData.attachments;

        // Add or replace signatures
        const newSignaturesWithTempIds = signatures?.map(sig => ({
          ...sig,
          id: `temp-sig-${sig.type}-${Math.random()}`
        })) || [];

        const updatedSignatures = signatures
          ? [
            ...prevData.signatures.filter(
              (existingSig) => !signatures.some((newSig) =>
                newSig.type === existingSig.type ||
                (newSig.type === 'checkin_guest' && existingSig.type === 'guest_checkin_type') ||
                (newSig.type === 'checkin_staff' && existingSig.type === 'staff_checkin_type') ||
                (newSig.type === 'checkout_guest' && existingSig.type === 'guest_checkout_type') ||
                (newSig.type === 'checkout_staff' && existingSig.type === 'staff_checkout_type')
              )
            ),
            ...newSignaturesWithTempIds,
          ]
          : prevData.signatures;

        return {
          ...prevData,
          ...restOfPayload,
          items: updatedItems,
          attachments: updatedAttachments,
          signatures: updatedSignatures
        };
      });

      setSaveStatus('success');
    } catch (e) {
      const message = e instanceof Error ? e.message : "An unknown error occurred";
      setSaveError(message);
      setSaveStatus('error');
      throw e; // re-throw to allow page-level error handling if needed
    } finally {
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [admissionData]);

  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-full min-h-[60vh]">
          <div className="flex flex-col items-center">
            <svg className="animate-spin -ml-1 mr-3 h-10 w-10 text-brand-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-xl text-slate-600 mt-4">Loading Admission Data...</span>
          </div>
        </div>
      );
    }
    if (error) {
      return <div className="text-center text-red-600 font-semibold p-8">Error fetching data: {error}</div>;
    }
    if (!admissionData) {
      return <div className="text-center text-slate-600 font-semibold p-8">No admission data available.</div>;
    }

    if (isPrinting) {
      return <ReportView data={admissionData} />;
    }

    const commonProps = {
      data: admissionData,
      onUpdate: handleUpdate,
      isSaving: saveStatus === 'saving',
      isClientView,
      onError: reportError,
    };

    switch (activePageId) {
      case 'admission':
        return <AdmissionPage {...commonProps} onContinue={() => setActivePageId('serviceAgreement')} />;
      case 'serviceAgreement':
        return <ServiceAgreementPage onContinue={() => setActivePageId('schedule1')} />;
      case 'schedule1':
        return <Schedule1Page {...commonProps} onContinue={() => setActivePageId('schedule2')} />;
      case 'schedule2':
        return <Schedule2Page {...commonProps} onContinue={() => setActivePageId('schedule3')} />;
      case 'schedule3':
        return <Schedule3Page {...commonProps} onContinue={() => setActivePageId('acceptance')} />;
      case 'acceptance':
        return <AcceptancePage {...commonProps} onAccept={() => setActivePageId('pdpa')} />;
      case 'pdpa':
        return <PDAPage {...commonProps} onAccept={() => setActivePageId('welcome')} />;
      case 'welcome':
        return <WelcomePage />;
      default:
        return <AdmissionPage {...commonProps} onContinue={() => setActivePageId('serviceAgreement')} />;
    }
  }, [activePageId, admissionData, isLoading, error, handleUpdate, saveStatus, isClientView, isPrinting]);

  const activePageIndex = pages.findIndex(p => p.id === activePageId);

  const pagesWithStatus: PageWithStatus[] = pages.map((page, index) => {
    let status: PageStatus;
    if (index < activePageIndex) {
      status = 'completed';
    } else if (index === activePageIndex) {
      status = 'active';
    } else {
      status = 'upcoming';
    }
    return {
      ...page,
      status,
      completeness: completeness ? completeness[page.id] : 0
    };
  });

  const totalCompleteness = calculateTotalCompleteness(completeness);

  return (
    <div className={`flex flex-col md:flex-row min-h-screen bg-slate-50 font-sans ${isPrinting ? 'print:block' : ''}`}>
      {!isPrinting && (
        <Sidebar
          pages={pagesWithStatus}
          onPageChange={setActivePageId}
          isClientView={isClientView}
          onClientViewChange={setIsClientView}
          totalCompleteness={totalCompleteness}
          onDownloadReport={() => setIsPrinting(true)}
        />
      )}
      <main className={`flex-1 overflow-y-auto ${isPrinting ? 'p-0' : 'p-4 sm:p-6 lg:p-8 print:p-0'}`}>
        <div className={`w-full ${isPrinting ? '' : 'max-w-7xl mx-auto'}`}>
          <div className={`${isPrinting ? '' : 'bg-white rounded-2xl shadow-lg p-6 sm:p-8 md:p-10 h-full print:shadow-none print:rounded-none'}`}>
            {renderContent()}
          </div>
        </div>
      </main>
      <SaveStatusDisplay status={saveStatus} message={saveError} />
    </div>
  );
};

export default App;